import { useState, ReactNode } from "react";
import { CopyIcon } from "../../../../assets/icons";
import TabSelector from "../../../../common/components/tab-selector/TabSelector";
import { formatCurrency, formatTimestamp, handleCopy } from "../../../../utils";
import style from "../../customer.module.scss";
import { AdminCustomerModel } from "../../../../model/customer/customer";
import { useGetCustomerDetailsForUtilityAdminQuery, useSetCustomerBlacklistedMutation } from "../../../../service/customer-service";
import { SkeletonGrid } from "../../../../common/components/xepp/skeleton-loader/SkeletonLoader";
import { CustomerTransactions } from "./CustomerTransactions";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import blacklistStyle from "./BlacklistCustomer.module.scss";
import StatusBadge, { StatusBadgeVariant } from "../../../../common/components/xepp/status-badge/StatusBadge";

type IndicatorBadge = { variant: StatusBadgeVariant; label: string };

const NA = "N/A";

// Energy profile: "Completed" (positive) | "Partially submitted" (neutral) | N/A (neutral).
const energyProfileBadge = (status?: string | null): IndicatorBadge =>
	status === "Completed"
		? { variant: "completed", label: "Completed" }
		: status === "Partially submitted"
			? { variant: "unknown", label: "Partially submitted" }
			: { variant: "unknown", label: NA };

// NIN: verified (positive) | N/A (neutral).
const ninBadge = (isVerified?: boolean | null): IndicatorBadge =>
	isVerified === true ? { variant: "completed", label: "Verified" } : { variant: "unknown", label: "N/A (Not available)" };

// RTL is a two-state indicator per the spec: opted-in => Active; anything else
// (opted-out, or never set / null) => Inactive ("not opted in").
const remoteTokenBadge = (prefer?: boolean | null): IndicatorBadge =>
	prefer === true ? { variant: "completed", label: "Active" } : { variant: "unknown", label: "Inactive" };

// A single label/value cell inside a field group. `hidden` renders an invisible
// spacer so columns stay aligned across groups with fewer than four items.
const Field = ({ label, children, hidden }: { label?: string; children?: ReactNode; hidden?: boolean }) =>
	hidden ? (
		<div className={style.field} aria-hidden="true" />
	) : (
		<div className={style.field}>
			<div className={style.fieldLabel}>{label}</div>
			<div className={style.fieldValue}>{children}</div>
		</div>
	);

interface CustomerDetailPageProps {
	selectedCustomer: AdminCustomerModel;
	onUnlinkMeter: () => void;
}
export const CustomerDetailPage = ({ selectedCustomer, onUnlinkMeter }: CustomerDetailPageProps) => {
	const [selectedTab, setSelectedTab] = useState(1);
	const [showConfirm, setShowConfirm] = useState(false);

	const tabs = [
		{ id: 1, title: "Transactions", onClick: () => {} },
		// { id: 2, title: "Service Requests", onClick: () => {} },
	];

	const { data: customer, isLoading, isError, refetch } = useGetCustomerDetailsForUtilityAdminQuery(selectedCustomer.userId);
	const [setBlacklisted, { isLoading: isBlacklistLoading }] = useSetCustomerBlacklistedMutation();

	const isBlacklisted = customer?.isBlacklisted ?? false;

	const handleConfirm = async () => {
		await setBlacklisted({ userId: selectedCustomer.userId, isBlacklisted: !isBlacklisted });
		setShowConfirm(false);
		refetch();
	};

    const renderTabContent = () => {
            switch (selectedTab) {
                case 1:
                    return <CustomerTransactions userId={selectedCustomer.userId} />;
                default:
                 return <CustomerTransactions userId={selectedCustomer.userId} />;
            }
        };

	return (
		<div className={style.customerContainer}>
			<div className={blacklistStyle.headerContainer}>
				<h2 className={style.header}>Customer Details</h2>

				<div className={blacklistStyle.actionBar}>
					{isBlacklisted && <span className={blacklistStyle.blacklistedBadge}>Blacklisted</span>}
					<button className={isBlacklisted ? blacklistStyle.removeBtn : blacklistStyle.blacklistBtn} onClick={() => setShowConfirm(true)} disabled={isLoading}>
						{isBlacklisted ? "Remove from blacklist" : "Blacklist customer"}
					</button>
					<button className={blacklistStyle.unlinkMeterBtn} onClick={onUnlinkMeter} disabled={isLoading}>
						Unlink Meter Number
					</button>
				</div>
			</div>

			{isLoading ? (
				<SkeletonGrid />
			) : isError ? (
				<div className={style.errorState} role="alert">
					<p className={style.errorMessage}>Couldn&apos;t load customer details. Please retry.</p>
					<button className={style.retryBtn} onClick={() => refetch()}>
						Retry
					</button>
				</div>
			) : (
				<div className={style.detailsCard}>
					<div className={style.detailsHeader}>
						<div className={style.avatar}>
							{`${customer?.firstName?.[0] ?? ""}${customer?.lastName?.[0] ?? ""}`.toUpperCase() || "?"}
						</div>
						<div className={style.customerName}>
							{customer?.firstName} {customer?.lastName}
						</div>
						<div className={style.headerMeta}>
							<span className={style.metaLabel}>Date Joined</span>
							<span className={style.metaValue}>{formatTimestamp(customer?.dateJoined ?? "") || NA}</span>
						</div>
					</div>

					<div className={style.fieldGroup}>
						<Field label="Email address">{customer?.username || NA}</Field>
						<Field label="Phone Number">
							{customer?.phoneNumber || NA}
							{customer?.phoneNumber ? <CopyIcon height={16} onClick={() => handleCopy(customer.phoneNumber)} /> : null}
						</Field>
						<Field label="Meter Number">{customer?.meterNumber || NA}</Field>
						<Field label="Account Number">{customer?.accountNumber || NA}</Field>
					</div>

					<div className={style.fieldGroup}>
						<Field label="Wallet Account Number">{customer?.walletAccountNumber || NA}</Field>
						<Field label="Wallet Balance">{customer?.walletBalance != null ? formatCurrency(customer.walletBalance) : NA}</Field>
						<Field label="Last Login">{formatTimestamp(customer?.lastLogin ?? "") || NA}</Field>
						<Field hidden />
					</div>

					<div className={style.fieldGroup}>
						<Field label="NIN Status">
							{(() => {
								const b = ninBadge(customer?.isNinVerified);
								return <StatusBadge variant={b.variant}>{b.label}</StatusBadge>;
							})()}
						</Field>
						<Field label="Energy Profile">
							{(() => {
								const b = energyProfileBadge(customer?.energyProfileStatus);
								return <StatusBadge variant={b.variant}>{b.label}</StatusBadge>;
							})()}
						</Field>
						<Field label="Remote Token Load">
							{(() => {
								const b = remoteTokenBadge(customer?.preferRemoteTokenLoad);
								return <StatusBadge variant={b.variant}>{b.label}</StatusBadge>;
							})()}
						</Field>
						<Field hidden />
					</div>
				</div>
			)}
			<div className={style.tabSelectorWrapper}>
				<div className={style.tabContainer}>
					<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
				</div>
				{renderTabContent()}
			</div>

			{showConfirm && (
				<CommonDialog onCloseDialog={() => setShowConfirm(false)}>
					<div className={blacklistStyle.modal}>
						<div className={blacklistStyle.modalHeader}>
							<h3 className={blacklistStyle.modalTitle}>{isBlacklisted ? "Remove from Blacklist" : "Blacklist Customer"}</h3>
							<p className={blacklistStyle.modalSubtitle}>This action cannot be undone</p>
						</div>
						<div className={blacklistStyle.modalDivider} />
						<div className={blacklistStyle.modalBody}>
							<p className={blacklistStyle.modalQuestion}>
								{isBlacklisted ? "Are you sure you want to remove this customer from the blacklist?" : "Are you sure you want to blacklist this customer?"}
							</p>
						</div>
						<div className={blacklistStyle.modalDivider} />
						<div className={blacklistStyle.modalFooter}>
							<button className={blacklistStyle.cancelBtn} onClick={() => setShowConfirm(false)} disabled={isBlacklistLoading}>
								No
							</button>
							<button className={blacklistStyle.confirmBtn} onClick={handleConfirm} disabled={isBlacklistLoading}>
								Yes
							</button>
						</div>
					</div>
				</CommonDialog>
			)}
		</div>
	);
};
